import React, { useEffect } from "react"
import { Controller, UseFormMethods, useFieldArray, useWatch } from "react-hook-form"
import { FormulaireSchemaType, WorkTypeEnum } from "../../../lib/formulaireContext/validation/formulaire.schema"
import { Paper, Typography, Grid, Button, Box, Divider, ButtonBase } from "@material-ui/core"
import { FormTextField } from "../Fields/FormTextField"
import { FormFileField } from "../Fields/FormFileField"
import AddIcon from "@material-ui/icons/Add"
import DeleteIcon from "@material-ui/icons/Delete"
import { FormRadioField } from "../Fields/FormRadioField"
import { WorkBeneficiariesSection } from "./WorkBeneficiariesSection"
import { useDownloadMusic } from "../../../lib/formulaireContext/hooks/useDownloadMusic"
type FieldConfig = {
    name: string;
    label: string;
    readOnly?: boolean;
    required?: boolean;
    helperText?: string;
}

type WorkType = {
    value: string;
    label: string;
}

const workFields: FieldConfig[] = [
    { name: "title", label: "Titre de l'œuvre", required: true },
    { name: "usedInSpotDuration", label: "Durée d'utilisation dans le spot (en sec.)", required: true },
    { name: "workType", label: "Type d'œuvre", required: true },
    { name: "iswcCode", label: "Code ISWC" },
    { name: "isrcCode", label: "Code ISRC" },
    { name: "workCode", label: "Code œuvre" },
]

const workTypes: WorkType[] = [
    { value: WorkTypeEnum.EXISTING, label: "Œuvre préexistante" },
    { value: WorkTypeEnum.NEW, label: "Œuvre nouvelle" },
    { value: WorkTypeEnum.SIGNATURE, label: "Signature musicale" },
]

export const WorksSection = ({ form }: { form: UseFormMethods<FormulaireSchemaType> }) => {
    const { handleDownloadMusic } = useDownloadMusic()
    const { control, formState, watch } = form

    const { fields, append, remove } = useFieldArray({
        control,
        name: "works"
    });

    const handleAdd = () => {
        append({
            title: "",
            usedInSpotDuration: 0,
            workType: WorkTypeEnum.NEW,
            iswcCode: "",
            isrcCode: "",
            workCode: "",
            file_name: "",
            work_id: null,
            beneficiaries: []
        });
        setTimeout(() => {
            form.trigger(`works`)
        }, 1)
    };

    // Fonction pour déterminer si le champ de fichier doit être affiché
    const shouldShowFileField = (workType: string, file_name: string) => {
        return (workType === WorkTypeEnum.NEW || workType === WorkTypeEnum.SIGNATURE) && !file_name;
    };

    const handleRemoveFile = (index: number) => {
        const field = fields[index]
        form.setValue(`works.[${index}].audioFile`, null);
        form.setValue(`works.[${index}].file_name`, null);
        field.audioFile = null
        field.file_name = null
    }


    return (
        <Paper elevation={2} style={{ padding: 18, marginBottom: 26, marginTop: 26 }}>
            <Typography variant="h5" component="h2" gutterBottom>
                Listing des Œuvres :
            </Typography>
            <Divider style={{ marginBottom: '20px' }} />

            {fields.map((field, index) => {
                // Observer le type d'œuvre pour conditionnellement afficher le champ de fichier
                const workType = watch(`works.[${index}].workType`);

                return (
                    <Box key={field.id} mb={4}>
                        {index > 0 && <Divider style={{ margin: '20px 0' }} />}

                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                Œuvre {index + 1}
                            </Typography>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<DeleteIcon />}
                                onClick={() => remove(index)}
                                size="small"
                            >
                                Supprimer
                            </Button>
                        </Box>

                        <Grid container spacing={3}>
                            <Controller
                                control={control}
                                name={`works.[${index}].work_id`}
                                defaultValue=""
                                render={(field) => {
                                    return (
                                        <input
                                            type="hidden"
                                            name={`works.[${index}].work_id`}
                                            defaultValue={field.value}
                                        />
                                    )
                                }}
                            />
                            {['title', 'usedInSpotDuration'].map(fieldKey => {
                                const fieldConfig = workFields.find(f => f.name === fieldKey)
                                if (!fieldConfig) return null
                                return (
                                    <Grid item xs={12} sm={6} key={`${field.id}-${fieldConfig.name}`}>
                                        <FormTextField
                                            label={fieldConfig.label}
                                            name={`works.[${index}].${fieldConfig.name}`}
                                            control={control}
                                            disabled={fieldConfig.readOnly}
                                            required={fieldConfig.required}
                                            formState={formState}
                                            helperText={fieldConfig.helperText}
                                        />
                                    </Grid>
                                )
                            })}
                            <FormRadioField
                                label="Type d'œuvre"
                                name={`works.[${index}].workType`}
                                control={control}
                                formState={formState}
                                choices={workTypes}
                                onChange={(newValue) => {
                                    // Déclencher la validation immédiatement
                                    form.trigger(`works.[${index}].workCode`);

                                    // Redéclencher après un délai pour s'assurer que l'état est à jour
                                    setTimeout(() => {
                                        form.trigger(`works.[${index}].workCode`);
                                    }, 50);
                                }}
                            />
                            {['iswcCode', 'isrcCode', 'workCode'].map(fieldKey => {
                                const fieldConfig = workFields.find(f => f.name === fieldKey)
                                if (!fieldConfig) return null
                                return (
                                    <Grid item xs={12} sm={6} key={`${field.id}-${fieldConfig.name}`}>
                                        <FormTextField
                                            label={fieldConfig.label}
                                            name={`works.[${index}].${fieldConfig.name}`}
                                            control={control}
                                            disabled={fieldConfig.readOnly}
                                            required={fieldConfig.required}
                                            formState={formState}
                                            helperText={fieldConfig.helperText}
                                        />
                                    </Grid>
                                )
                            })}

                            {/* Champ de fichier audio conditionnel */}
                            {shouldShowFileField(workType as string, field.file_name) && (
                                <Grid item xs={12}>
                                    <FormFileField
                                        label="Fichier audio"
                                        name={`works.[${index}].audioFile`}
                                        control={control}
                                        formState={formState}
                                        helperText="Formats acceptés: MP3, WAV, MPEG, MPGA, M4A, OGG, FLAC, AAC"
                                        accept="audio/mp3,audio/wav,audio/mpeg,audio/mpga,audio/m4a,audio/ogg,audio/flac,audio/aac"
                                    />
                                </Grid>
                            )}
                            {field.file_name && (

                                <Grid item xs={12}>
                                    <Controller
                                        control={control}
                                        name={`works.[${index}].file_name`}
                                        defaultValue=""
                                        render={(field) => {
                                            return (
                                                <input
                                                    type="hidden"
                                                    name={`works.[${index}].file_name`}
                                                    defaultValue={field.value}
                                                />
                                            )
                                        }}
                                    />
                                    <Grid container alignItems="center">
                                        <Typography style={{ marginRight: 10 }} variant="body1" color="textSecondary">
                                            Fichier audio:
                                            <ButtonBase
                                                onClick={() => handleDownloadMusic(field.work_id)}
                                                style={{ marginLeft: 10, textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold' }}
                                            >
                                                {field.file_name}
                                            </ButtonBase>
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleRemoveFile(index)}
                                            size="small"
                                        >
                                            Supprimer
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                        <WorkBeneficiariesSection workIndex={index} form={form} />
                    </Box>
                )
            })}

            {fields.length === 0 && (
                <Box textAlign="center" py={3}>
                    <Typography variant="body1" color="textSecondary">
                        Aucune œuvre ajoutée. Cliquez sur le bouton ci-dessous pour ajouter une œuvre.
                    </Typography>
                    <Typography variant="body1" color="error">
                        Vous devez ajouter au moins une œuvre.
                    </Typography>
                </Box>
            )}

            <Box mt={3} display="flex" justifyContent="center">
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Ajouter une œuvre
                </Button>
            </Box>
        </Paper>
    )
}